import { useMemo } from "react";

import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { useWorkspaceUsers } from "src/api/taktile/queries";
import { useCaseQueues } from "src/cases/queries/queues";
import { useAllCaseVersionsCombinedWithTemplates } from "src/cases/queries/templates";
import { OrganizationUser } from "src/clients/taktile-api";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { getUserName } from "src/utils/user";

export const useCaseAnalyticsLabels = (): LabelDictionaries => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();

  const { data: users = [] } = useWorkspaceUsers(orgId, wsId, {
    include_deactivated: true,
  });
  const { data: queues = [] } = useCaseQueues();
  const { data: templateVersions = [] } =
    useAllCaseVersionsCombinedWithTemplates();

  return useMemo<LabelDictionaries>(
    () => ({
      queue_id: Object.fromEntries(
        queues.map((queue) => [queue.id, queue.name]),
      ),
      template_id: Object.fromEntries(
        templateVersions.map((tv) => [tv.templateId, tv.templateName]),
      ),
      template_version_id: Object.fromEntries(
        templateVersions.map((tv) => [tv.versionId, tv.versionName]),
      ),
      user_id: Object.fromEntries(
        users
          .filter((user): user is OrganizationUser => !!user)
          .map((user) => [user.id, getUserName(user) ?? user.id]),
      ),
    }),
    [queues, templateVersions, users],
  );
};
